<template>
  <div>
    <!-- Conditionally render LevelComplete component -->
    <LevelComplete v-if="gameCompleted" />
    
    <div
      v-else
      class="meal-game"
    >
      <h1 class="instruction">
        Make your {{ currentLevel.name.toLowerCase() }}!
      </h1>

      <div class="game-area">
        <div class="item-list">
          <div
            v-for="(item, index) in currentLevel.items"
            :key="index"
            class="draggable-item"
            :draggable="true"
            :style="draggedItem === item ? draggedStyle : null"
            @dragstart="handleDragStart($event, item)"
            @click="speakOutLoud(item.name)"
            @touchstart.prevent="handleTouchStart($event, item)"
            @touchmove.prevent="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            {{ getItemDisplay(item) }}
          </div>
        </div>

        <h2 class="instruction">
          🍽️ Your Plate ({{ plateItems.length }}/{{ currentLevel.maxItems }})
        </h2>

        <div class="plate-area">
          <div
            class="plate"
            @dragover.prevent
            @drop="handleDrop($event)"
          >
            <div class="plate-items">
              <div
                v-for="(item, index) in plateItems"
                :key="index"
                class="plate-item"
                :style="getPlateItemStyle(index)"
              >
                {{ getItemDisplay(item) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
import LevelComplete from '../LevelComplete.vue';
import { speechMixin } from "@/mixins/speechMixin.js";

const emojiMapping = {
  Eggs: "🍳",
  Toast: "🍞",
  Bacon: "🥓",
  Pancakes: "🥞",
  Cereal: "🥣",
  Milk: "🥛",
  Juice: "🧃",
  Oatmeal: "🥣",
  Yogurt: "🥛",
  Apple: "🍎",
  Sandwich: "🥪",
  Salad: "🥗",
  Soup: "🍲",
  Pasta: "🍝",
  Rice: "🍚",
  Chicken: "🍗",
  Fish: "🐟",
  Vegetables: "🥦",
  Cake: "🍰",
  "Ice Cream": "🍦",
  Pie: "🥧",
  Pudding: "🍮",
  StrawBerry: "🍓",
  Cookies: "🍪",
  Brownies: "🍫",
};

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      levels: [
        {
          name: "Breakfast",
          maxItems: 4,
          items: [
            { name: "Eggs" },
            { name: "Toast" },
            { name: "Bacon" },
            { name: "Pancakes" },
            { name: "Cereal" },
            { name: "Milk" },
            { name: "Juice" },
            { name: "Oatmeal" },
            { name: "Yogurt" },
            { name: "Apple" },
          ],
        },
        {
          name: "Lunch",
          maxItems: 4,
          items: [
            { name: "Sandwich" },
            { name: "Salad" },
            { name: "Soup" },
            { name: "Pasta" },
            { name: "Rice" },
            { name: "Chicken" },
            { name: "Fish" },
            { name: "Vegetables" },
          ],
        },
        {
          name: "Dessert",
          maxItems: 2,
          items: [
            { name: "Cake" },
            { name: "Ice Cream" },
            { name: "Pie" },
            { name: "Pudding" },
            { name: "StrawBerry" },
            { name: "Cookies" },
            { name: "Brownies" },
          ],
        },
        {
          name: "Fun Dish",
          maxItems: 6,
          items: [
            { name: "Bacon" },
            { name: "Pancakes" },
            { name: "Milk" },
            { name: "Apple" },
            { name: "Sandwich" },
            { name: "Pasta" },
            { name: "Cookies" },
            { name: "Brownies" },
          ],
        },
      ],
      currentLevelIndex: 0,
      plateItems: [],
      message: "",
      draggedItem: null,
      draggedStyle: {},
      gameCompleted: false, // New data property
    };
  },
  computed: {
    currentLevel() {
      return this.levels[this.currentLevelIndex];
    },
  },
  methods: {
    getItemDisplay(item) {
      return emojiMapping[item.name] || item.name;
    },
    handleDragStart(event, item) {
      event.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    handleDrop(event) {
      const itemData = event.dataTransfer.getData("text/plain");
      let item;
      try {
        item = JSON.parse(itemData);
      } catch (e) {
        console.error("Invalid item data");
        return;
      }
      this.addItemToPlate(item);
    },
    handleItemDropped(item) {
      this.addItemToPlate(item);
    },
    addItemToPlate(item) {
      const itemInLevel = this.currentLevel.items.find(
        (i) => i.name === item.name
      );
      if (!itemInLevel) {
        this.message = `❌ ${item.name} is not available for ${this.currentLevel.name}.`;
        this.playSound("incorrect");
      } else if (this.plateItems.find((i) => i.name === item.name)) {
        this.message = `❌ You already added ${item.name} to your plate.`;
        this.playSound("incorrect");
      } else if (this.plateItems.length >= this.currentLevel.maxItems) {
        this.message = `❌ You have reached the maximum items for ${this.currentLevel.name}.`;
        this.playSound("incorrect");
      } else {
        this.message = `✅ You added ${item.name} to your plate.`;
        this.playSound("correct");
        this.speakOutLoud(item.name);
        this.currentLevel.items = this.currentLevel.items.filter(
          (i) => i.name !== item.name
        );
        this.plateItems.push(item);
      }
      setTimeout(() => {
        this.message = "";
      }, 2000);

      if (this.plateItems.length === this.currentLevel.maxItems) {
        setTimeout(() => {
          this.advanceLevel();
        }, 3000);
      }
    },
    advanceLevel() {
      if (this.currentLevelIndex < this.levels.length - 1) {
        this.currentLevelIndex++;
        this.plateItems = [];
        this.message = `👍 Great job! Now let's make ${this.currentLevel.name}.`;
        setTimeout(() => {
          this.message = "";
        }, 2000);
      } else {
        // All levels completed
        this.gameCompleted = true; // Show LevelComplete component
        this.playSound("correct"); // Optional: play a sound for completion
        setTimeout(() => {
          this.$router.push('/fruits-and-veggies');
        }, 3000); // 3 seconds delay
      }
    },
    playSound(type) {
      let url;
      if (type === "correct") {
        url =
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a";
      } else if (type === "incorrect") {
        url =
          "https://codeskulptor-demos.commondatastorage.googleapis.com/pang/arrow.mp3";
      }
      if (url) {
        const audio = new Audio(url);
        audio.play();
      }
    },
    getPlateItemStyle(index) {
      const totalItems = this.plateItems.length;
      const angleIncrement = (2 * Math.PI) / totalItems;
      const angle = index * angleIncrement - Math.PI / 2;
      const radius = 35;
      const x = 50 + radius * Math.cos(angle);
      const y = 50 + radius * Math.sin(angle);
      return {
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        transform: "translate(-50%, -50%)",
      };
    },
    handleTouchStart(event, item) {
      event.preventDefault();
      this.draggedItem = item;

      // Set the initial position of the dragged item
      const touch = event.touches[0];
      this.draggedStyle = {
        position: 'fixed',
        left: touch.clientX + 'px',
        top: touch.clientY + 'px',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      if (!this.draggedItem) return;
      const touch = event.touches[0];
      this.draggedStyle.left = touch.clientX + 'px';
      this.draggedStyle.top = touch.clientY + 'px';
    },
    handleTouchEnd(event) {
      if (!this.draggedItem) return;

      const touch = event.changedTouches[0];

      const dropTarget = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      if (
        dropTarget &&
        (dropTarget.classList.contains("plate") ||
          dropTarget.closest(".plate"))
      ) {
        this.addItemToPlate(this.draggedItem);
      } else {
        this.message = `❌ ${this.draggedItem.name} was not dropped on the plate.`;
        this.playSound("incorrect");
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
      this.draggedItem = null;
      this.draggedStyle = {};
    },
  },
};
</script>

<style scoped>
.meal-game {
  text-align: center;
}

.game-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.item-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 20px;
}

.draggable-item {
  margin: 10px;
  padding: 15px;
  background-color: #f3e5f5;
  border-radius: 10px;
  cursor: grab;
  font-size: 3rem;
  user-select: none;
  transition: transform 0.3s;
}

.draggable-item:active {
  cursor: grabbing;
}

.draggable-item:hover {
  transform: scale(1.1);
}

.draggable-item[style*="position: fixed"] {
  pointer-events: none;
}

.plate-area {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.plate {
  width: 300px;
  height: 300px;
  border: 2px solid #888;
  border-radius: 50%;
  position: relative;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  outline-offset: -60px;
  outline: 5px solid lightgray;
}

.plate:hover {
  background-color: #f0f0f0;
}

.plate-items {
  position: relative;
  width: 100%;
  height: 100%;
}

.plate-item {
  position: absolute;
  font-size: 6rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.instruction {
  font-size: 2rem;
  margin-bottom: 20px;
}

.message {
  font-size: 1.5rem;
  margin-top: 20px;
}

/* Optional: Style for LevelComplete component overlay */
.level-complete-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
</style>